/**
 * CRM-Email Bridge Service
 * Connects CRM contacts with Email campaigns for unified outreach
 */

import { buildApiUrl, fetchJSON, validateRequiredParams } from './crmApi';
import contactsService from './contactsService';
import activitiesService from './activitiesService';
import emailService from '../emailService';

// Types
export interface EmailCampaignEnrollment {
  id: string;
  crm_contact_id: string;
  email_campaign_id: string;
  organization_id: string;
  enrolled_at: string;
  enrolled_by?: string;
  status: 'active' | 'paused' | 'completed' | 'unsubscribed';
  source: 'manual' | 'workflow' | 'auto';
  metadata?: Record<string, any>;
}

export interface EmailEngagement {
  id: string;
  crm_contact_id: string;
  organization_id: string;
  email_thread_id?: string;
  email_message_id?: string;
  engagement_type: 'sent' | 'opened' | 'clicked' | 'replied' | 'bounced';
  campaign_id?: string;
  campaign_name?: string;
  subject?: string;
  snippet?: string;
  engaged_at: string;
  metadata?: Record<string, any>;
}

export interface CampaignEnrollmentRequest {
  crm_contact_id: string;
  email_campaign_id: string;
  organization_id: string;
  source?: 'manual' | 'workflow' | 'auto';
  metadata?: Record<string, any>;
}

export interface EmailEngagementStats {
  total_sent: number;
  total_opened: number;
  total_clicked: number;
  total_replied: number;
  total_bounced: number;
  open_rate: number;
  reply_rate: number;
  engagement_score: number;
  last_engagement?: string;
  active_campaigns: number;
}

const crmEmailBridgeService = {
  /**
   * Enroll a CRM contact in an email campaign
   */
  async enrollInCampaign(request: CampaignEnrollmentRequest): Promise<EmailCampaignEnrollment> {
    validateRequiredParams(
      request,
      ['crm_contact_id', 'email_campaign_id', 'organization_id'],
      { service: 'crmEmailBridgeService.enrollInCampaign' }
    );

    // 1. Get CRM contact data
    const contactResult = await contactsService.get(request.crm_contact_id, request.organization_id);
    const contact = contactResult.data;
    
    if (!contact || !contact.email) {
      throw new Error('Contact not found or has no email address');
    }

    // 2. Get campaign details
    const campaign = await emailService.getCampaign(request.email_campaign_id);
    
    if (!campaign) {
      throw new Error('Email campaign not found');
    }

    // 3. Create/update email contact
    const emailContact = await this.syncContactToEmail(request.crm_contact_id, request.organization_id);

    // 4. Enroll in email campaign
    await emailService.addContactsToCampaign(request.email_campaign_id, [emailContact.id]);

    // 5. Record enrollment in CRM
    const url = buildApiUrl('/api/crm/email-bridge/enrollments');
    const enrollment = await fetchJSON<{ data: EmailCampaignEnrollment }>(url, {
      method: 'POST',
      body: JSON.stringify({
        crm_contact_id: request.crm_contact_id,
        email_campaign_id: request.email_campaign_id,
        organization_id: request.organization_id,
        source: request.source || 'manual',
        metadata: {
          ...request.metadata,
          campaign_name: campaign.name,
          contact_email: contact.email
        }
      }),
      description: 'Enroll contact in campaign',
      context: request as any
    });

    // 6. Create CRM activity
    await activitiesService.create({
      organization_id: request.organization_id,
      entity_type: 'contact',
      entity_id: request.crm_contact_id,
      type: 'note',
      content: `📧 Enrolled in email campaign: ${campaign.name}\n\nCampaign ID: ${request.email_campaign_id}\nSource: ${request.source || 'manual'}`
    });

    return enrollment.data;
  },

  /**
   * Unenroll a contact from a campaign
   */
  async unenrollFromCampaign(
    enrollmentId: string,
    organizationId: string
  ): Promise<void> {
    const url = buildApiUrl(`/api/crm/email-bridge/enrollments/${enrollmentId}`);
    
    await fetchJSON(url, {
      method: 'DELETE',
      body: JSON.stringify({ organization_id: organizationId }),
      description: 'Unenroll from campaign',
      context: { enrollmentId, organizationId }
    });
  },

  /**
   * Get all campaign enrollments for a contact
   */
  async getContactEnrollments(
    contactId: string,
    organizationId: string
  ): Promise<EmailCampaignEnrollment[]> {
    const url = buildApiUrl('/api/crm/email-bridge/enrollments', {
      crm_contact_id: contactId,
      organization_id: organizationId
    });

    const result = await fetchJSON<{ data: EmailCampaignEnrollment[] }>(url, {
      description: 'Get contact enrollments',
      context: { contactId, organizationId }
    });

    return result.data || [];
  },

  /**
   * Sync CRM contact to email system
   */
  async syncContactToEmail(contactId: string, organizationId: string): Promise<any> {
    const contactResult = await contactsService.get(contactId, organizationId);
    const contact = contactResult.data;
    
    if (!contact || !contact.email) {
      throw new Error('Contact not found or has no email');
    }

    // Get company name if available
    let companyName: string | undefined;
    if (contact.company_id) {
      try {
        const companiesService = await import('./companiesService');
        const companyResult = await companiesService.default.get(contact.company_id, organizationId);
        companyName = companyResult.data?.name;
      } catch (error) {
        console.warn('Could not fetch company name:', error);
      }
    }

    // Create or update email contact
    const emailContact = await emailService.createContact({
      email: contact.email,
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      company: companyName,
      position: contact.job_title,
      status: 'active' as const,
      notes: JSON.stringify({
        crm_contact_id: contactId,
        crm_organization_id: organizationId,
        lead_score: contact.lead_score,
        lifecycle_stage: contact.lifecycle_stage,
        persona: contact.persona,
        sync_source: 'crm',
        synced_at: new Date().toISOString()
      })
    });

    return emailContact;
  },

  /**
   * Record email engagement in CRM
   */
  async recordEngagement(engagement: Omit<EmailEngagement, 'id'>): Promise<EmailEngagement> {
    validateRequiredParams(
      engagement,
      ['crm_contact_id', 'organization_id', 'engagement_type', 'engaged_at'],
      { service: 'crmEmailBridgeService.recordEngagement' }
    );

    const url = buildApiUrl('/api/crm/email-bridge/engagements');
    
    const result = await fetchJSON<{ data: EmailEngagement }>(url, {
      method: 'POST',
      body: JSON.stringify(engagement),
      description: 'Record email engagement',
      context: engagement
    });

    // Also create CRM activity for significant engagements
    if (['replied', 'opened'].includes(engagement.engagement_type)) {
      const activityContent = engagement.engagement_type === 'replied'
        ? `💬 Replied to email: ${engagement.subject || 'Email'}`
        : `👀 Opened email: ${engagement.subject || 'Email'}`;

      await activitiesService.create({
        organization_id: engagement.organization_id,
        entity_type: 'contact',
        entity_id: engagement.crm_contact_id,
        type: 'email',
        content: activityContent + `\n\nCampaign: ${engagement.campaign_name || 'N/A'}\nThread ID: ${engagement.email_thread_id || 'N/A'}`
      });
    }

    return result.data;
  },

  /**
   * Get email engagement history for a contact
   */
  async getContactEngagements(
    contactId: string,
    organizationId: string,
    limit: number = 50
  ): Promise<EmailEngagement[]> {
    const url = buildApiUrl('/api/crm/email-bridge/engagements', {
      crm_contact_id: contactId,
      organization_id: organizationId,
      limit: limit.toString()
    });

    const result = await fetchJSON<{ data: EmailEngagement[] }>(url, {
      description: 'Get contact engagements',
      context: { contactId, organizationId, limit }
    });

    return result.data || [];
  },

  /**
   * Get engagement statistics for a contact
   */
  async getEngagementStats(
    contactId: string,
    organizationId: string
  ): Promise<EmailEngagementStats> {
    const url = buildApiUrl('/api/crm/email-bridge/stats', {
      crm_contact_id: contactId,
      organization_id: organizationId
    });

    const result = await fetchJSON<{ data: EmailEngagementStats }>(url, {
      description: 'Get engagement stats',
      context: { contactId, organizationId }
    });

    return result.data;
  },

  /**
   * Sync email thread to CRM activities
   * Records all messages in a thread as CRM activities
   * TODO: Implement when emailService.getThreadById is available
   */
  async syncThreadToCrm(
    emailThreadId: string,
    organizationId: string
  ): Promise<void> {
    // TODO: Implement when Unified Inbox API is ready
    console.warn('syncThreadToCrm not yet implemented - emailService.getThreadById not available');
    return;
    
    /* Implementation placeholder for when API is ready:
    
    try {
      const thread = await emailService.getThreadById(emailThreadId);
      // ... rest of implementation
    } catch (error) {
      console.error('Error syncing thread to CRM:', error);
      throw error;
    }
    */
  },

  /**
   * Get available email campaigns for enrollment
   */
  async getAvailableCampaigns(): Promise<any[]> {
    try {
      const campaigns = await emailService.getCampaigns();
      return campaigns.filter((c: any) => c.status === 'active' || c.status === 'draft');
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      return [];
    }
  }
};

export default crmEmailBridgeService;
